// React
import { memo, useEffect, useState } from "react";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { DataWithPagination } from "ui/components/Table/consts";
import { initialPagination } from "ui/components/Table/consts";

// Services & API
import { createVehicleInsuranceAPI, getVehicleInsuranceAPI, updateVehicleInsuranceAPI } from "services/api/calculations/vehicleinsurance/api";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { initialDataWithPagination } from "services/api/consts";
import { getVehicleGroupsAPI } from "services/api/calculations/vehiclegroup/api";

//types
import { VehicleGroupRow } from "services/api/calculations/vehiclegroup/types";

// Hooks & Helpers
import { useHandleDropdownChange, useHandleTextFieldChange } from "services/forms/hooks";
import { useTranslate } from "helpers";

// Types & Constants
import { VehicleInsuranceEditProps } from "./types";
import { VehicleInsuranceEditFormValues } from "services/api/calculations/vehicleinsurance/types";
import { initialVehicleInsuranceEditForm } from "services/api/calculations/vehicleinsurance/consts";
import { CALCULATION_VEHICLE_INSURANCE_EDIT_FORM_SCHEMA } from "./validation";



const VehicleInsuranceEditCard: React.FC<
  VehicleInsuranceEditProps
> = (props) => {
  const { t } = useTranslate();
  const { vehicleInsuranceId, onSuccess } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleGroups, setVehicleGroups] = useState<
    DataWithPagination<VehicleGroupRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] =
    useState<VehicleInsuranceEditFormValues>(
      initialVehicleInsuranceEditForm
    );

  useEffect(() => {
    if (vehicleInsuranceId) {
      fetchVehicleInsurance(vehicleInsuranceId);
      fetchVehicleGroups();
    }
  }, [vehicleInsuranceId]);

  const fetchVehicleInsurance = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleInsuranceEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleInsuranceAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchVehicleGroups = async () => {
    setIsLoading(true);

    let result;

    try {
      result = await getVehicleGroupsAPI(initialPagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setVehicleGroups(result);
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_VEHICLE_INSURANCE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!vehicleInsuranceId) {
        result = await updateVehicleInsuranceAPI(
          vehicleInsuranceId,
          formValues
        );
      } else {
        result = await createVehicleInsuranceAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const vehicleGroupItems: DropdownOption[] = vehicleGroups.data.map((i) => {
    return {
      value: i.id.toString(),
      text: i.name,
    };
  });

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex flex-row gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="fromKw"
                      label={t(
                        "admin.calculation.vehicle.insurance.edit.fromkw"
                      )}
                      placeholder={t(
                        "admin.calculation.vehicle.insurance.edit.fromkw.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.fromKw}
                      value={formValues.fromKw}
                      required
                      endIcon=""
                      groupAppend="kw"
                    ></TextField>
                  </div>
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="toKw"
                      label={t("admin.calculation.vehicle.insurance.edit.tokw")}
                      placeholder={t(
                        "admin.calculation.vehicle.insurance.edit.tokw.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.toKw}
                      value={formValues.toKw}
                      required
                      endIcon=""
                      groupAppend="kw"
                    ></TextField>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="price"
                      label={t(
                        "admin.calculation.vehicle.insurance.edit.price"
                      )}
                      placeholder={t(
                        "admin.calculation.vehicle.insurance.edit.price.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.price}
                      value={formValues.price}
                      required
                      endIcon=""
                      groupAppend="€"
                    ></TextField>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(VehicleInsuranceEditCard);
